import { cn } from '@rocco/utils/cn';
import { detailCta } from '~/ui-rocco/pages/detail-shared/detail-cta';
import {
    Button,
    Caption,
    ProductInfo as AriaProductInfo,
    useAppDisplayModeContext,
    useStyles,
    pxToRem,
} from '@archipro-design/aria';
import { Link, useFetcher } from 'react-router';
import type { ReactNode } from 'react';
import { useState, useEffect } from 'react';
import type { ModuleProduct } from '@modules/page-builder/api/get-module-product.server';
import type { PageBuilderGetModuleProductAction } from '~/routes/remix-api.page-builder.get-module-product';
import { QUERY_MODULE_PRODUCT } from '~/routes/remix-api.page-builder.get-module-product';
import { AriaProductInfoStyle } from '@modules/page-builder/component/common/product-info/ProductInfo.style';
import type { ColumnDimensionType } from '@modules/page-builder/types';
import { SingleImageWithProductContainer } from '@modules/page-builder/component/detail/detail-single-image-shell/DetailSingleImageShell.style';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { buildVariantDots } from '@modules/tile/util/build-variant-dots';

interface ProductInfoProps {
    productId: number;
    variant?: '01' | '02';
    image?: ReactNode;
    clickable: boolean;
    dimension?: ColumnDimensionType;
    moduleType?: string;
    className?: string;
    /** Project detail revamp: the platform CTA in place of the legacy button. */
    isProjectRevamp?: boolean;
}

const ProductInfo = ({
    productId,
    variant,
    image,
    clickable,
    dimension,
    moduleType,
    className,
    isProjectRevamp,
}: ProductInfoProps) => {
    const { mobile } = useAppDisplayModeContext();
    const fetcher = useFetcher<PageBuilderGetModuleProductAction>();
    const isPageBuilder =
        typeof window !== 'undefined' &&
        (window.location.pathname.includes('edit') ||
            window.location.pathname.includes('page-builder'));

    const { css } = useStyles({
        dimension,
        variant,
        moduleType,
        isPageBuilder,
    });

    const [productInfo, setProductInfo] = useState<ModuleProduct>();

    const fetchProductInfo = () => {
        if (!productInfo && fetcher.state === 'idle') {
            fetcher.load(
                `${QUERY_MODULE_PRODUCT}?productId=${encodeURIComponent(productId)}`,
            );
        }
    };

    useEffect(() => {
        fetchProductInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (fetcherIsDone(fetcher) && fetcher.data) {
            setProductInfo(fetcher.data);
        }
    }, [fetcher]);

    const { productLink, variants, additionalVariantCount, ...rest } =
        productInfo || {};

    const { variantDots, adjustedAdditionalCount } = buildVariantDots({
        variants,
        additionalVariantCount,
        mobile,
    });

    // The tagged product's own CTA. On the revamp it is the platform button on
    // a dark section — the hairline step, because a gallery can carry one of
    // these under every tagged photograph and the page's own actions have to
    // stay louder than them.
    //
    // Dressed onto the shorthand rather than nested inside it: this slot builds
    // its own button around whatever it is handed, so an already-styled pill
    // passed as content would sit in a second one. The overrides are important
    // because the legacy button's fill and padding are injected unlayered and
    // would otherwise win on source order alone.
    // No link of its own: the whole tile is already the product's link, and an
    // anchor inside an anchor is not markup a browser will keep.
    const cta = isProjectRevamp ? (
        <span className={detailCta('outlined-dark', undefined, 's')}>
            View product
        </span>
    ) : clickable && productLink ? (
        <Button
            as={Link}
            to={productLink}
            size={14}
            variables={{
                buttonPadding: pxToRem(9),
                buttonSize14TextFontSize: pxToRem(12),
            }}
        >
            VIEW PRODUCT
        </Button>
    ) : (
        <Button
            size={14}
            variables={{
                buttonPadding: pxToRem(9),
                buttonSize14TextFontSize: pxToRem(12),
            }}
        >
            VIEW PRODUCT
        </Button>
    );

    const productDetails = productInfo ? (
        <AriaProductInfo
            {...rest}
            className={css(AriaProductInfoStyle)}
            productName={
                <Caption variant="04" maximumLines={1}>
                    {productInfo.productName}
                </Caption>
            }
            professionalName={{
                children: (
                    <Caption variant="04" maximumLines={1}>
                        {productInfo.professionalName}
                    </Caption>
                ),
            }}
            variants={variantDots}
            additionalVariantCount={adjustedAdditionalCount}
            variant={variant || '01'}
            {...(variant === '02' ? { cta } : {})}
        />
    ) : null;

    const visualContent = (
        <>
            {image}
            {productDetails}
        </>
    );

    const appliedClass =
        moduleType === 'FeaturedProductModule' ? (className ?? '') : '';

    return (
        <div
            className={cn(
                css(SingleImageWithProductContainer),
                appliedClass,
                // The slot builds its own button around whatever it is handed,
                // so the pill inside it would sit in a second one. The wrapper
                // gives up its fill and padding and the pill is the button.
                isProjectRevamp &&
                    '[&_.ui-button]:h-auto! [&_.ui-button]:rounded-none! [&_.ui-button]:bg-transparent! [&_.ui-button]:p-0!',
            )}
        >
            {clickable && productLink ? (
                <Link to={productLink}>{visualContent}</Link>
            ) : (
                visualContent
            )}
        </div>
    );
};

export default ProductInfo;
